/**
 * Versioning for the three direction artifacts — Purpose, Vision and Mission.
 *
 * ⚠️⚠️ WHY THIS EXISTS. The Act I remap (`docs/ACT_I_REMAP.md`) makes ch4 produce
 * explicit **V1** statements that a later Act rewrites once the first cycle has
 * produced evidence. `bestSelf` could already do that — it carries `version` and
 * `bestSelfHistory` — but Purpose/Vision/Mission were plain strings, so a rewrite
 * overwrote the original with no trace. "You wrote this before you had evidence,
 * here is V2" is unsayable without a lineage to point at.
 *
 * ⭐ The current TEXT stays where it always was (`purposeStatement`, `vision`,
 * `missions[domainId]`). This module only adds the metadata beside it. That is
 * deliberate: ~15 components read those fields today and none of them need to
 * change, and a second copy of the text would be a second answer waiting to
 * disagree with the first.
 *
 * Mirrors the `setBestSelf` rules on purpose — one idiom for every direction
 * artifact in the app.
 */

/**
 * ⚠️ `'mission'` is the PER-DOMAIN mission (seven of them); `'personal-mission'`
 * is the single one that sits beside Purpose and Vision.
 *
 * ⭐ They are the same KIND of statement at two altitudes, which is why both keep
 * the word Mission in the UI — "Your Mission" (one) and "Domain Missions"
 * (seven). They need separate keys here only because one is discriminated by
 * domain and the other is singular: without its own kind, `directionKey` would
 * hand the personal mission the key `"mission:"` — a domain mission belonging to
 * a domain called empty-string.
 */
export type DirectionKind = 'purpose' | 'vision' | 'mission' | 'personal-mission';

/** Where the wording came from, so an AI draft is never mistaken for the user's own. */
export type DirectionSource = 'ai' | 'composed' | 'edited';

/**
 * A dated direction statement.
 *
 * `reviewedAt` is separate from `writtenAt` for the same reason it is on
 * `BestSelfStatement`: confirming that a statement still holds is a real act,
 * and it should clear "needs attention" without pretending it was rewritten.
 */
export interface DirectionVersion {
  kind: DirectionKind;
  /** Set only when `kind === 'mission'` — which domain this mission belongs to. */
  domainId?: string;
  statement: string;
  version: number;
  /**
   * ⚠️ For an **adopted** record this is when versioning adopted the text, NOT
   * when the user wrote it — that date was never stored. Check `adopted` before
   * showing this as an authoring date.
   */
  writtenAt: string;
  reviewedAt: string;
  source?: DirectionSource;
  /**
   * ⭐ True when this record was synthesised from text that already existed
   * before versioning shipped. See `reviseDirection` — without this the very
   * first rewrite would silently discard the statement it replaced, which is
   * precisely the lineage the whole feature exists to keep.
   */
  adopted?: boolean;
}

/**
 * The map key for a direction artifact. DOMAIN missions are per-domain, so they
 * need a discriminator; purpose, vision and the personal mission are singular.
 */
export function directionKey(kind: DirectionKind, domainId?: string): string {
  return kind === 'mission' ? `mission:${domainId ?? ''}` : kind;
}

export interface ReviseInput {
  kind: DirectionKind;
  domainId?: string;
  /** The text currently on the store — may predate versioning entirely. */
  previousText: string;
  /** The version record for that text, if it has one. */
  previousVersion: DirectionVersion | undefined;
  /** The incoming text. */
  nextText: string;
  now: string;
  source?: DirectionSource;
}

export interface ReviseResult {
  /** The version record for the new current text. */
  current: DirectionVersion;
  /** The record to push onto history, or null when nothing was superseded. */
  superseded: DirectionVersion | null;
  /** True when the text was unchanged — a confirmation, not a new version. */
  confirmed: boolean;
}

/**
 * Decide what a write to a direction artifact means.
 *
 * Returns `null` for empty input: clearing a statement is not a version, and
 * writing a blank V2 over a real V1 would destroy the lineage rather than extend
 * it. Callers should treat null as "do nothing".
 */
export function reviseDirection(input: ReviseInput): ReviseResult | null {
  const { kind, domainId, previousText, previousVersion, nextText, now, source } = input;
  const text = nextText.trim();
  if (!text) return null;

  // ⭐⭐ ADOPTION. Everyone who used the app before this shipped has a real
  // statement and no version record. Treating that as "no previous version"
  // would make their next edit v1 and drop the original on the floor — the one
  // statement Act II most wants to contrast against. So the existing text is
  // adopted as v1 first, and the edit then supersedes it as v2.
  const previous: DirectionVersion | undefined =
    previousVersion ??
    (previousText.trim()
      ? {
          kind,
          domainId,
          statement: previousText.trim(),
          version: 1,
          writtenAt: now,
          reviewedAt: now,
          source: 'edited',
          adopted: true,
        }
      : undefined);

  // Same text = a confirmation, not a new version. Bumping the version on an
  // unchanged statement would fake growth that did not happen. (Same rule as
  // setBestSelf — deliberately.)
  if (previous && previous.statement === text) {
    return {
      current: { ...previous, reviewedAt: now, ...(source ? { source } : {}) },
      superseded: null,
      confirmed: true,
    };
  }

  return {
    current: {
      kind,
      domainId,
      statement: text,
      version: (previous?.version ?? 0) + 1,
      writtenAt: now,
      reviewedAt: now,
      source,
    },
    superseded: previous ?? null,
    confirmed: false,
  };
}

/** Superseded versions of one artifact, newest first. */
export function historyFor(
  history: DirectionVersion[],
  kind: DirectionKind,
  domainId?: string,
): DirectionVersion[] {
  const key = directionKey(kind, domainId);
  return history.filter(v => directionKey(v.kind, v.domainId) === key);
}

/**
 * Whether a statement is old enough to be worth revisiting.
 *
 * ⚠️ Measured from `reviewedAt`, not `writtenAt` — confirming a statement still
 * holds is what keeps it current. A statement written a year ago and confirmed
 * last week is not stale; asking again would punish the user for having been
 * consistent.
 */
export function isDirectionStale(
  version: DirectionVersion | undefined,
  staleAfterDays: number,
  now: Date = new Date(),
): boolean {
  if (!version) return false;
  const reviewed = new Date(version.reviewedAt).getTime();
  if (Number.isNaN(reviewed)) return false;
  return now.getTime() - reviewed > staleAfterDays * 24 * 60 * 60 * 1000;
}
